import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import Skeleton from "react-loading-skeleton";
import { useSearchParams, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { FleetrifyModal } from "../../../Modal/modal";
import { FleetrifyTextareaField } from "../../../Input/textArea";
import FleetrifyRadioButton from "../../../Input/radio";
import { FleetrifyButton } from "../../../Button/customButton";
import {
    useRejectInvitationMutation,
    useGetSingleInvitedUserQuery,
} from "../../../../redux/services/inviteApi";
import { rejectionReasons } from "../../../../utils/mock";
import { FLEETRIFYROUTEPATH } from "../../../../routes/routesPath";

const RejectUserInviteModal: React.FC<{ isOpen: boolean; onClose: () => void }> = ({
    isOpen,
    onClose,
}) => {
    const [selectedReason, setSelectedReason] = useState<string>("");
    const [otherReason, setOtherReason] = useState<string>("");

    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const userID = searchParams.get("id");
    // const viewParam = searchParams.get("view");

    // const {
    //     data: invitedUserData,
    //     isLoading: isLoadingInvitedUser,
    //     isError: isErrorInvitedUser,
    // } = useGetSingleInvitedUserQuery(userID ?? "", {
    //     skip: !userID || viewParam !== "reject",
    // });

    const {
        data: invitedUserData,
        isLoading: isLoadingInvitedUser,
        isError: isErrorInvitedUser,
    } = useGetSingleInvitedUserQuery(userID ?? "", {
        skip: !userID,
    });
    const [rejectInvitation, { isLoading }] = useRejectInvitationMutation();

    useEffect(() => {
        if (!userID) {
            toast.error("Invalid or missing invitation link.");
            navigate(FLEETRIFYROUTEPATH.LOGIN);
        }
    }, [userID, navigate]);

    const invitedUser = invitedUserData?.data;

    const isSubmittable =
        selectedReason && (selectedReason !== "Others" || !!otherReason.trim());

    const handleReject = async () => {
        if (!isSubmittable) {
            toast.error("Please select or specify a reason before continuing.");
            return;
        }

        try {
            const reason =
                selectedReason === "Others" ? otherReason.trim() : selectedReason;

            await rejectInvitation({ id: String(userID), reason }).unwrap();

            toast.success("Invitation rejected successfully!", {
                style: {
                    background: "#e6ffed",
                    color: "#007b55",
                    border: "1px solid #00b37e",
                },
            });

            onClose();
            navigate(FLEETRIFYROUTEPATH.LOGIN);
        } catch (err: any) {
            toast.error(err?.data?.message || "Failed to reject invitation.", {
                style: {
                    background: "#ffe5e5",
                    color: "#ef4444",
                    border: "1px solid #ff4d4f",
                },
            });
        }
    };

    if (!isOpen) return null;

    if (isLoadingInvitedUser) {
        return (
            <FleetrifyModal
                disableClose
                isOpen={isOpen}
                onClose={onClose}
                overlayClassName="z-[9999]"
                enableOutsideClick={false}
                className="max-w-2xl h-fit"
            >
                <div className="flex flex-col justify-center items-center h-[40vh] space-y-4">
                    <Skeleton width={200} height={25} />
                    <Skeleton width={300} height={20} />
                    <Skeleton width={250} height={20} />
                </div>
            </FleetrifyModal>
        );
    }

    if (isErrorInvitedUser) {
        return (
            <FleetrifyModal isOpen={isOpen} onClose={onClose}>
                <div className="p-10 text-center">
                    <p className="text-red-600 font-medium">
                        Failed to load invited user details.
                    </p>
                    <button
                        onClick={() => navigate(FLEETRIFYROUTEPATH.LOGIN)}
                        className="mt-4 px-6 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
                    >
                        Go Back
                    </button>
                </div>
            </FleetrifyModal>
        );
    }

    return (
        <FleetrifyModal
            disableClose
            isOpen={isOpen}
            onClose={onClose}
            className="max-w-2xl max-h-[85vh] hide-scrollbar"
            overlayClassName="z-[9999]"
            enableOutsideClick={false}
            aria-labelledby="reject-invite-title"
        >
            <div className="flex flex-col h-full max-h-[80vh] px-10">
                {/* header */}
                <div className="space-y-5 pt-4">
                    <h1
                        id="reject-invite-title"
                        className="text-[#00032A] text-[20px] font-bold"
                    >
                        Dear{" "}
                        {!invitedUser?.first_name && !invitedUser?.last_name ? (
                            <Skeleton width={120} height={20} />
                        ) : (
                            <span className="font-extrabold capitalize">
                                {invitedUser?.first_name} {invitedUser?.last_name}
                            </span>
                        )}
                    </h1>
                    <p className="text-[#00032A]">
                        Please specify the reason you are rejecting this invitation.
                    </p>
                </div>

                {/* body */}
                <div className="flex-1 overflow-y-auto mt-2 hide-scrollbar space-y-6 py-6">
                    {/* user Info */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700">
                            First Name
                        </label>
                        <input
                            type="text"
                            value={invitedUser?.first_name ?? ""}
                            disabled
                            className="w-full mt-1 px-4 py-2 border rounded-lg bg-gray-100 cursor-not-allowed"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700">
                            Last Name
                        </label>
                        <input
                            type="text"
                            value={invitedUser?.last_name ?? ""}
                            disabled
                            className="w-full mt-1 px-4 py-2 border rounded-lg bg-gray-100 cursor-not-allowed"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700">
                            Email
                        </label>
                        <input
                            type="email"
                            value={invitedUser?.email ?? ""}
                            disabled
                            className="w-full mt-1 px-4 py-2 border rounded-lg bg-gray-100 cursor-not-allowed"
                        />
                    </div>

                    {/* rejection reason */}
                    <div className="space-y-3">
                        <h6 className="font-medium">Select a Reason</h6>
                        {rejectionReasons.map((reason) => (
                            <label key={reason} className="flex items-center space-x-2">
                                <FleetrifyRadioButton
                                    name="rejectReason"
                                    value={reason}
                                    checked={selectedReason === reason}
                                    onValueChange={() => setSelectedReason(reason)}
                                />
                                <span className="text-sm font-medium">{reason}</span>
                            </label>
                        ))}

                        <AnimatePresence>
                            {selectedReason === "Others" && (
                                <motion.div
                                    key="other-reason"
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    transition={{ duration: 0.2 }}
                                >
                                    <FleetrifyTextareaField
                                        id="otherReason"
                                        value={otherReason}
                                        onChange={(e) => setOtherReason(e.target.value)}
                                        placeholder="Specify your other reason"
                                    />
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>

                {/* Footer */}
                <div className="sticky bottom-0 bg-white py-4 mt-6 flex justify-end border-t border-gray-200">
                    <FleetrifyButton
                        onClick={handleReject}
                        disabled={!isSubmittable || isLoading}
                        loading={isLoading}
                        className={`px-10 py-2 rounded-md transition-colors ${!isSubmittable || isLoading
                            ? "bg-red-400 text-white cursor-not-allowed"
                            : "bg-[#EB5757] hover:bg-red-600 text-white"
                            }`}
                    >
                        {isLoading ? "Processing..." : "Reject Invitation"}
                    </FleetrifyButton>
                </div>
            </div>
        </FleetrifyModal>
    );
};

export default RejectUserInviteModal;
